import {useEffect, useMemo, useState} from 'react';
import {MenuItem, Select, SelectChangeEvent} from '@mui/material';
import useUpdateEffect from 'ahooks/es/useUpdateEffect';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_PaginationState,
  MRT_RowData,
  MRT_TableOptions,
  MRT_VisibilityState,
  useMaterialReactTable,
} from 'material-react-table';

import {
  getGlobalPageSize,
  loadDataFromLocalStorage,
  PAGE_SIZE_OPTIONS,
  saveDataToLocalStorage,
  saveGlobalPageSize,
} from '../../../utils';
import {ClickToSortIcon} from './icons/ClickToSortIcon';
import {SortIcon} from './icons/SortIcon';

import {COLOR__BACK, COLOR__LINE} from '@/theme/colors';
import {tablePaperStyles} from '@/theme/components/table';
import typographyOptions from '@/theme/typography';

const sortIconCommonStyles = {ml: 1} as const;

interface TablePersistentSettings {
  columnOrder?: MRT_ColumnOrderState;
  columnVisibility?: MRT_VisibilityState;
}

const getColumnId = <TData extends MRT_RowData>(
  col: MRT_ColumnDef<TData>,
): string | null => {
  if (typeof col.id === 'string' && col.id) {
    return col.id;
  }

  if (typeof col.accessorKey === 'string' && col.accessorKey) {
    return col.accessorKey;
  }

  return null;
};

const mergeColumnOrder = (
  nextColumnIds: string[],
  savedColumnOrder?: MRT_ColumnOrderState,
): MRT_ColumnOrderState => {
  if (!savedColumnOrder?.length) {
    return nextColumnIds;
  }

  const nextColumnIdSet = new Set(nextColumnIds);
  const normalizedSavedColumnOrder = savedColumnOrder.filter(colId =>
    nextColumnIdSet.has(colId),
  );
  const missingColumnIds = nextColumnIds.filter(
    colId => !normalizedSavedColumnOrder.includes(colId),
  );

  return [...normalizedSavedColumnOrder, ...missingColumnIds];
};

interface Props<TData extends MRT_RowData> extends MRT_TableOptions<TData> {
  localStorageKeyForSettings?: string;
  columns: MRT_ColumnDef<TData>[];
  data: TData[];
  error?: Error | null;
}

export const MrTable = <TData extends MRT_RowData>({
  localStorageKeyForSettings,
  error,
  enableColumnOrdering,
  enableColumnFilters,
  enableHiding,
  enablePagination,
  enableGlobalFilter,
  initialState,
  state,
  onPaginationChange,
  ...restProps
}: Props<TData>) => {
  const isPaginationEnabled = !!enablePagination;
  const currentPageSize = state?.pagination?.pageSize ?? 10;

  useEffect(() => {
    if (!isPaginationEnabled || !onPaginationChange) return;
    const saved = getGlobalPageSize();
    if (saved !== null && saved !== currentPageSize) {
      (onPaginationChange as (updater: MRT_PaginationState) => void)({
        pageIndex: 0,
        pageSize: saved,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistentSettings: TablePersistentSettings | null =
    localStorageKeyForSettings
      ? (loadDataFromLocalStorage<TablePersistentSettings>(
          localStorageKeyForSettings,
        ) ?? null)
      : null;

  const colNames = useMemo(() => {
    return restProps.columns
      .map(col => getColumnId(col))
      .filter(Boolean) as string[];
  }, [restProps.columns]);

  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(
    mergeColumnOrder(colNames, persistentSettings?.columnOrder),
  );

  const defaultColumnVisibility = {} as MRT_VisibilityState;
  for (const colName of colNames) {
    defaultColumnVisibility[colName] = true;
  }
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {
      ...defaultColumnVisibility,
      ...persistentSettings?.columnVisibility,
    },
  );

  useUpdateEffect(() => {
    if (localStorageKeyForSettings) {
      saveDataToLocalStorage<TablePersistentSettings>(
        {
          ...persistentSettings,
          columnVisibility,
        },
        localStorageKeyForSettings,
      );
    }
  }, [columnVisibility]);

  const table = useMaterialReactTable({
    enableGlobalFilter: enableGlobalFilter ?? false,
    enableColumnActions: false,
    enableColumnFilters: enableColumnFilters ?? false,
    enablePagination: isPaginationEnabled,
    paginationDisplayMode: 'pages',
    muiPaginationProps: isPaginationEnabled
      ? {
          color: 'primary',
          shape: 'rounded',
          showRowsPerPage: false,
          variant: 'outlined',
        }
      : undefined,
    enableSorting: true,
    enableFullScreenToggle: true,
    enableDensityToggle: false,
    enableBottomToolbar: isPaginationEnabled,
    renderBottomToolbarCustomActions: isPaginationEnabled
      ? () => (
          <Select
            size="small"
            value={currentPageSize}
            onChange={(e: SelectChangeEvent<number>) => {
              const newSize = Number(e.target.value);
              saveGlobalPageSize(newSize);
              if (onPaginationChange) {
                (onPaginationChange as (updater: MRT_PaginationState) => void)({
                  pageIndex: 0,
                  pageSize: newSize,
                });
              }
            }}
            sx={{fontSize: 14, height: 32}}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <MenuItem key={size} value={size}>
                {size} / page
              </MenuItem>
            ))}
          </Select>
        )
      : undefined,
    onPaginationChange,
    enableHiding,
    enableTopToolbar:
      !!enableHiding || !!restProps.renderTopToolbarCustomActions,
    enableColumnOrdering: enableColumnOrdering,
    enableColumnDragging: enableColumnOrdering,
    onColumnOrderChange: enableColumnOrdering
      ? newColumnOrder => {
          setColumnOrder(newColumnOrder);

          if (localStorageKeyForSettings) {
            saveDataToLocalStorage<TablePersistentSettings>(
              {
                ...persistentSettings,
                columnOrder: newColumnOrder as MRT_ColumnOrderState,
              },
              localStorageKeyForSettings,
            );
          }
        }
      : undefined,
    onColumnVisibilityChange: setColumnVisibility,
    positionToolbarAlertBanner: 'head-overlay',
    muiToolbarAlertBannerProps: error
      ? {
          color: 'error',
          children: error.message,
        }
      : undefined,
    muiTablePaperProps: {
      sx: {
        ...tablePaperStyles,
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: 1.5,
        backgroundColor: COLOR__BACK,
        ...typographyOptions.b1bold,
        '& .Mui-TableHeadCell-Content-Labels': {
          flexDirection: 'row',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: 1,
        borderBottom: `1px solid ${COLOR__LINE}`,
      },
    },
    muiFilterTextFieldProps: {
      variant: 'outlined',
    },
    icons: {
      SyncAltIcon: () => <ClickToSortIcon sx={sortIconCommonStyles} />,
      ArrowDownwardIcon: ({className}: {className: string}) => {
        return (
          <SortIcon
            sx={sortIconCommonStyles}
            sortOrder={
              className.includes('MuiTableSortLabel-iconDirectionDesc')
                ? 'desc'
                : 'asc'
            }
          />
        );
      },
    },
    initialState: {
      ...initialState,
      showColumnFilters: enableColumnFilters,
      density: 'compact',
      showGlobalFilter: enableGlobalFilter,
    },
    state: {
      ...state,
      columnOrder,
      columnVisibility,
      showAlertBanner: !!error,
    },
    globalFilterFn: 'contains', // turn off fuzzy matching and use simple contains filter function
    ...restProps,
  });

  return <MaterialReactTable table={table} />;
};

export const rightAlignmentColumnProps = {
  muiTableHeadCellProps: {
    align: 'right',
  },
  muiTableBodyCellProps: {
    align: 'right',
  },
} as const;

export const centerAlignmentColumnProps = {
  muiTableHeadCellProps: {
    align: 'center',
  },
  muiTableBodyCellProps: {
    align: 'center',
  },
} as const;
